import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Sparkles, Flame, ShieldCheck, BookOpen, AlertCircle, TrendingUp, Target } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import Layout from '@/components/Layout';
import ProgressCircle from '@/components/ProgressCircle';
import CategoryBadge from '@/components/CategoryBadge';
import LevelBadge from '@/components/LevelBadge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { pillarTitle } from '@/constants/pillars';
import { DASHBOARD } from '@/constants/testIds';

export default function Dashboard() {
	const { user } = useAuth();
	const [latest, setLatest] = useState(null);
	const [progress, setProgress] = useState({ months: [], badges: [], assessments_count: 0 });
	const [education, setEducation] = useState([]);
	const [projection, setProjection] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const [a, p, e, pr] = await Promise.all([
					api.get('/assessments/latest'),
					api.get('/progress'),
					api.get('/education'),
					api.get('/coach/projection'),
				]);
				setLatest(a.data?.assessment || null);
				setProgress(p.data || { months: [], badges: [], assessments_count: 0 });
				setEducation((e.data?.items || []).slice(0, 6));
				setProjection(pr.data || null);
			} catch (err) {
				toast.error(formatApiError(err));
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	const overall = projection?.current_score ?? latest?.overall ?? 0;
	const baseOverall = latest?.overall ?? 0;
	const category = projection?.category || latest?.category || 'Critical';
	const pillars = latest?.pillars || {};
	const sortedPillars = Object.entries(pillars).sort((a, b) => b[1] - a[1]);
	const strengths = sortedPillars.slice(0, 2);
	const risks = sortedPillars.slice(-2).reverse();
	const months = progress.months || [];
	const latestMonthScore = months.length ? months[months.length - 1].score : null;
	const prevMonthScore = months.length > 1 ? months[months.length - 2].score : null;
	const delta = latestMonthScore != null && prevMonthScore != null ? latestMonthScore - prevMonthScore : null;

	return (
		<Layout>
			<div data-testid={DASHBOARD.root} className="enter-stagger space-y-8">
				{/* Welcome header */}
				<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
					<div>
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Welcome back</div>
						<h1 className="mt-2 text-3xl sm:text-4xl font-heading font-bold tracking-tight text-slate-900">
							Hello, {user?.name?.split(' ')[0] || 'Family'} 👋
						</h1>
						<p className="text-slate-600 mt-1">Tiny acts of preparedness, repeated with love.</p>
					</div>
					<Link to="/assessment">
						<Button data-testid={DASHBOARD.startAssessmentButton} className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-12 px-6 font-semibold gap-2">
							<Sparkles className="w-4 h-4" /> {latest ? 'Retake assessment' : 'Take your first assessment'}
							<ArrowRight className="w-4 h-4" />
						</Button>
					</Link>
				</div>

				{/* Score + mission grid */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Score card */}
					<div className="card-soft lg:col-span-1 p-8 flex flex-col items-center justify-center">
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">Family Resilience Score</div>
						<ProgressCircle value={overall} sublabel={category} label="out of 100" testId={DASHBOARD.scoreCircle} />
						<div className="mt-6 flex items-center gap-3 flex-wrap justify-center">
							<CategoryBadge category={category} />
							{projection?.boost > 0 && (
								<span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">
									<TrendingUp className="w-4 h-4" /> +{projection.boost} from tasks
								</span>
							)}
							{delta != null && projection?.boost === 0 && (
								<span className={`inline-flex items-center gap-1 text-sm font-semibold ${delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
									<TrendingUp className={`w-4 h-4 ${delta >= 0 ? '' : 'rotate-180'}`} />
									{delta >= 0 ? '+' : ''}{delta} this month
								</span>
							)}
						</div>
						{projection?.boost > 0 && (
							<div className="mt-2 text-xs text-slate-500">Base score: {baseOverall}</div>
						)}
						{!latest && (
							<p className="mt-4 text-sm text-slate-500 text-center max-w-xs">
								Take your first assessment to discover your score and a gentle plan to improve.
							</p>
						)}
					</div>

					{/* Mission + recommendation */}
					<div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
						<div className="card-soft p-6 sm:p-7 flex flex-col" data-testid={DASHBOARD.missionCard}>
							<div className="flex items-center gap-2 text-emerald-700">
								<Flame className="w-4 h-4" />
								<span className="text-xs font-bold uppercase tracking-[0.2em]">Monthly Mission</span>
							</div>
							<h3 className="mt-3 text-xl font-heading font-semibold text-slate-900 leading-snug">
								{latest?.coach?.monthly_mission || 'Have one 30-minute family preparedness conversation this month.'}
							</h3>
							<p className="mt-3 text-slate-600 text-sm leading-relaxed">
								{latest?.coach?.motivation || 'Small steps, repeated with love, become a sturdy shield for your family.'}
							</p>
							<div className="mt-auto pt-4">
								<Link to="/coach" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
									Talk to your Family Coach <ArrowRight className="w-4 h-4" />
								</Link>
							</div>
						</div>

						<div className="card-soft p-6 sm:p-7 flex flex-col">
							<div className="flex items-center gap-2 text-blue-800">
								<Sparkles className="w-4 h-4" />
								<span className="text-xs font-bold uppercase tracking-[0.2em]">Today&apos;s Recommendation</span>
							</div>
							<h3 className="mt-3 text-xl font-heading font-semibold text-slate-900 leading-snug">
								{latest?.coach?.top_priority ? `Focus on: ${latest.coach.top_priority}` : 'Start with one preparedness habit.'}
							</h3>
							<p className="mt-3 text-slate-600 text-sm leading-relaxed">
								{latest?.coach?.next_goal || 'Take the assessment so we can offer the smallest right next step.'}
							</p>
							<div className="mt-auto pt-4">
								<Link to="/library" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline">
									Explore the Library <ArrowRight className="w-4 h-4" />
								</Link>
							</div>
						</div>
					</div>
				</div>

				{/* Strengths & risks */}
				{latest && (
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="card-soft p-6 sm:p-7" data-testid={DASHBOARD.topStrengths}>
							<div className="flex items-center gap-2 text-emerald-700">
								<ShieldCheck className="w-4 h-4" />
								<span className="text-xs font-bold uppercase tracking-[0.2em]">Top Strengths</span>
							</div>
							<ul className="mt-4 space-y-3">
								{strengths.map(([k, v]) => (
									<li key={k} className="flex items-center justify-between">
										<span className="font-medium text-slate-800">{pillarTitle(k)}</span>
										<span className="text-emerald-700 font-bold tabular-nums">{v}</span>
									</li>
								))}
							</ul>
						</div>
						<div className="card-soft p-6 sm:p-7" data-testid={DASHBOARD.topRisks}>
							<div className="flex items-center gap-2 text-rose-700">
								<AlertCircle className="w-4 h-4" />
								<span className="text-xs font-bold uppercase tracking-[0.2em]">Room to Grow</span>
							</div>
							<ul className="mt-4 space-y-3">
								{risks.map(([k, v]) => (
									<li key={k} className="flex items-center justify-between">
										<span className="font-medium text-slate-800">{pillarTitle(k)}</span>
										<span className="text-rose-700 font-bold tabular-nums">{v}</span>
									</li>
								))}
							</ul>
						</div>
					</div>
				)}

				{/* Premium Coach projection (visible to all; CTA differs for Free vs Premium) */}
				{projection?.has_assessment && projection.next_level && (
					<div className="card-soft p-6 sm:p-7">
						<div className="flex items-start justify-between gap-4 flex-wrap">
							<div className="flex-1 min-w-[260px]">
								<div className="flex items-center gap-2 text-blue-800">
									<Target className="w-4 h-4" />
									<span className="text-xs font-bold uppercase tracking-[0.2em]">Family Resilience Coach™</span>
								</div>
								<h3 className="mt-2 text-2xl font-heading font-semibold text-slate-900 leading-tight">
									You are <span className="text-blue-800">{projection.points_to_next_level}</span> points away from becoming a <span className="text-emerald-700">{projection.next_level.title}</span>
								</h3>
								<p className="mt-2 text-slate-600 text-sm">Potential score: <span className="font-bold">{projection.potential_score}</span> · Current level: <span className="font-bold">{projection.current_level?.title}</span></p>
							</div>
							<LevelBadge level={projection.next_level} size="lg" />
						</div>
						{projection.top_opportunities?.length > 0 && (
							<div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2">
								{projection.top_opportunities.slice(0, 4).map((o) => (
									<div key={o.task_id} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50">
										<span className="inline-flex items-center justify-center min-w-[2.5rem] h-7 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm tabular-nums px-2">+{o.score_gain}</span>
										<span className="text-sm text-slate-800 flex-1 truncate">{o.title}</span>
									</div>
								))}
							</div>
						)}
						<div className="mt-5">
							<Link to="/coach">
								<Button className="rounded-full bg-emerald-600 hover:bg-emerald-700">
									Open my Improvement Plan <ArrowRight className="w-4 h-4 ml-2" />
								</Button>
							</Link>
						</div>
					</div>
				)}

				{/* Education cards */}
				<div>
					<div className="flex items-center justify-between mb-4">
						<h2 className="text-2xl font-heading font-semibold tracking-tight text-slate-900">Family Library</h2>
						<Link to="/library" className="text-sm font-semibold text-emerald-700 hover:underline">See all</Link>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
						{(loading ? new Array(3).fill(null) : education).map((item, i) => (
							<Link
								to={item ? `/library/${item.slug}` : '#'}
								key={item?.slug || i}
								className="card-soft p-6 hover:-translate-y-0.5 transition-transform group block"
							>
								<div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-4">
									<BookOpen className="w-5 h-5" />
								</div>
								<div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 mb-1">{item?.topic || 'Loading'}</div>
								<h4 className="font-heading text-lg font-semibold text-slate-900 leading-snug">{item?.title || '...'}</h4>
								<p className="mt-2 text-sm text-slate-600 line-clamp-2">{item?.summary || ''}</p>
							</Link>
						))}
					</div>
				</div>
			</div>
		</Layout>
	);
}
